import { Dialog, TabView, TabViewPanel } from '@myosh/odin-components';
import type { DataGridSettings, DialogRef, OdinDataRetrieval } from '@myosh/odin-components';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import type { Ref } from 'react';
import VersionLogs from './version-logs.component';
import NotificationLog from '../record/notification-log/notification-log';
import { useTranslation } from 'react-i18next';
import type { AuditLogData, AuditLogDataItem } from '../../@types/audit';
import type { GroupAuditLogData } from '../../@types/groups';

interface AuditLogModalProps {
  header: string;
  dataGridData: OdinDataRetrieval;
  dataGridSettings: DataGridSettings;
  recordId?: string;
  onRowSelectionChange: (selectedRow: AuditLogData | GroupAuditLogData) => Promise<Array<AuditLogDataItem>>;
}

const AuditLogModal = (
  { header, recordId, dataGridData, dataGridSettings, onRowSelectionChange }: AuditLogModalProps,
  ref: Ref<DialogRef>
) => {
  const { t } = useTranslation();

  const [pages, setPages] = useState<React.JSX.Element | React.JSX.Element[]>();
  const [isVisible, setIsVisible] = useState(false);

  const views = [
    {
      key: 'versions',
      header: t('versions'),
      component: (
        <VersionLogs
          auditGridData={dataGridData}
          dataGridSettings={dataGridSettings}
          onRowSelectionChange={onRowSelectionChange}
        />
      ),
      hidden: false,
    },
    {
      key: 'notifications',
      header: t('notification-log'),
      component: <NotificationLog recordId={recordId} />,
      hidden: Boolean(!recordId),
    },
  ];

  useEffect(() => {
    const filteredViews = views.filter((view) => view.hidden === false);

    if (filteredViews.length > 1) {
      setPages(
        <TabView>
          {filteredViews.map((view) => {
            return (
              <TabViewPanel key={view.key} header={view.header}>
                {view.component}
              </TabViewPanel>
            );
          })}
        </TabView>
      );
    } else {
      setPages(
        filteredViews.map((item) => {
          return <React.Fragment key={item.key}>{item.component}</React.Fragment>;
        })
      );
    }
  }, [dataGridData]);

  useImperativeHandle(ref, () => ({
    show: () => {
      setIsVisible(true);
    },
    close: () => {
      setIsVisible(false);
    },
  }));

  const onClose = () => {
    setIsVisible(false);
  };

  return (
    <>
      {isVisible && (
        <Dialog header={header} visible={isVisible} onClose={onClose} fullScreen>
          {pages}
        </Dialog>
      )}
    </>
  );
};

export default forwardRef(AuditLogModal);
